import * as _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { allIncludeOptionKeys } from '../../../../shared/reports';
import { useQuery } from '../../../contexts/RouterContext';
import {
  mapFilterStateToReportConfig,
} from '../../projects/generic/ReportGenerationV2/shared/components/Options/mapFilterStateToReportConfig';
import {
  mapReportConfigToOptionState,
} from '../../projects/generic/ReportGenerationV2/shared/components/Options/mapReportConfigToFilterState';
import type { PartialReportConfig } from '../../../../shared/reports/types';
import type { IncludeFilterOption } from '../../projects/generic/ReportGenerationV2/shared/types';
import type { ReportsV3QueryParams } from '../types';

import { CLEARED_CUSTOM_PARAMS, STANDARD_DEFAULTS } from './useCustomReportOptions';

type SavedReportOptionsResult = {
  currentConfig: PartialReportConfig;
  onApply: (config: PartialReportConfig | null) => boolean;
  isDirty: boolean;
};

type ComparableQueryParams = Record<string, string | string[]>;

const normalizeQueryParams = (params: ReportsV3QueryParams): ComparableQueryParams => {
  return Object.entries(params).reduce<ComparableQueryParams>((acc, [key, value]) => {
    if (value === undefined) {
      return acc;
    }

    if (Array.isArray(value)) {
      acc[key] = [...value].sort();
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});
};

/**
 * Bridges SavedOptionsContainer props with V3's URL query param state.
 * Converts between PartialReportConfig (used by saved options) and V3 query params.
 */
export const useSavedReportOptions = (): SavedReportOptionsResult => {
  const [query, setQuery] = useQuery<ReportsV3QueryParams>();
  const [appliedConfig, setAppliedConfig] = useState<PartialReportConfig | null>(null);

  // Based on the current filter state (in the URL query params), we need to build the current config.
  const currentConfig = useMemo(() => {
    const includeOptions = allIncludeOptionKeys.filter(
      (key) => query[key as keyof ReportsV3QueryParams] === 'true',
    ) as IncludeFilterOption[];
    const dependencyMetadataOptions = query.dependencyInfoOptions ?? [];
    const packageLabelExclusions = query.excludePackageLabels ?? [];
    return mapFilterStateToReportConfig(includeOptions, dependencyMetadataOptions, packageLabelExclusions);
  }, [query]);

  // After the user selects a saved report option, we need to update the URL query params to match the selected options.
  // This causes the proper checkboxes to be checked, and will trigger the preview to be updated.
  const onApply = useCallback(
    (config: PartialReportConfig | null) => {
      let nextQuery: ReportsV3QueryParams;

      if (config === null) {
        nextQuery = { ...query, ...CLEARED_CUSTOM_PARAMS, ...STANDARD_DEFAULTS };
        setAppliedConfig(null);
      } else {
        const { flatIncludeValues, licenseIncludeValues, dependencyMetadataValues, packageLabelExclusionValues } =
          mapReportConfigToOptionState(config);

        const params: Record<string, string | string[] | undefined> = { ...CLEARED_CUSTOM_PARAMS };

        [...flatIncludeValues, ...licenseIncludeValues].forEach((opt) => {
          params[opt.value] = 'true';
        });

        const depOpts = dependencyMetadataValues.map((v) => v.value);
        params.dependencyInfoOptions = depOpts.length > 0 ? depOpts : undefined;

        const labels = packageLabelExclusionValues.map((v) => String(v.value));
        params.excludePackageLabels = labels.length > 0 ? labels : undefined;

        nextQuery = { ...query, ...params };
        setAppliedConfig(
          mapFilterStateToReportConfig(
            [...flatIncludeValues, ...licenseIncludeValues].map((opt) => opt.value),
            depOpts,
            labels,
          ),
        );
      }

      const didChange = !_.isEqual(normalizeQueryParams(query), normalizeQueryParams(nextQuery));
      if (didChange) {
        setQuery(nextQuery);
      }

      return didChange;
    },
    [query, setQuery],
  );

  const isDirty = useMemo(() => {
    if (appliedConfig === null) {
      return false;
    }
    return !_.isEqual(currentConfig, appliedConfig);
  }, [currentConfig, appliedConfig]);

  return { currentConfig, onApply, isDirty };
};
